import { Link } from "react-router-dom";
import { Mail, Instagram, Twitter, ShieldCheck, MessageCircle } from "lucide-react";
import { LOGO_URL } from "../lib/api";
import { useAuth } from "../lib/auth";

export default function Footer() {
    const { user } = useAuth();
    const isAdmin = user?.role === "admin";

    return (
        <footer data-testid="site-footer" className="bg-[var(--bg-surface)] border-t border-[var(--border)] mt-24">
            <div className="bg-[var(--text-primary)] py-10">
                <div className="max-w-5xl mx-auto px-6 flex items-start gap-4 text-[var(--bg)]">
                    <ShieldCheck size={22} className="text-[var(--brand)] shrink-0 mt-1" />
                    <div>
                        <p className="font-ui font-semibold text-xs uppercase tracking-[0.22em] text-[var(--brand)] mb-2">For Laboratory Research Use Only</p>
                        <p className="text-sm leading-relaxed opacity-80">
                            All products offered by Aminoplex are intended exclusively for laboratory and research purposes.
                            These materials are not intended for human consumption, therapeutic use, diagnosis, treatment, or prevention of disease.
                            Customers are responsible for compliance with all applicable laws and regulations.
                        </p>
                    </div>
                </div>
            </div>

            <div className="max-w-7xl mx-auto px-6 lg:px-10 py-20 grid grid-cols-1 md:grid-cols-12 gap-10">
                <div className="md:col-span-4">
                    <div className="flex items-center gap-3 mb-6">
                        <img src={LOGO_URL} alt="Aminoplex" className="h-12 w-12 object-contain" />
                        <div>
                            <div className="font-display text-xl tracking-[0.08em] text-[var(--text-primary)] leading-none">AMINOPLEX</div>
                            <div className="font-ui text-[0.6rem] uppercase tracking-[0.22em] text-[var(--text-muted)] mt-1">Research Compounds</div>
                        </div>
                    </div>
                    <p className="text-sm text-secondary leading-relaxed mb-6 max-w-sm">
                        High-purity research peptides supported by complete analytical data and batch-specific documentation for advanced laboratory research.
                    </p>
                    <div className="flex items-center gap-2">
                        <a href="mailto:support@aminoplex.bio" className="btn btn-ghost" aria-label="Email support" data-testid="footer-email-link"><Mail size={14} /></a>
                        <a href="https://discord.com/invite/nMRuf628q" target="_blank" rel="noopener noreferrer" className="btn btn-ghost" aria-label="Join our Discord" data-testid="footer-discord-link"><MessageCircle size={14} /></a>
                        <a href="#" className="btn btn-ghost"><Instagram size={14} /></a>
                        <a href="#" className="btn btn-ghost"><Twitter size={14} /></a>
                    </div>
                </div>

                <div className="md:col-span-2">
                    <div className="data-label mb-4">Shop</div>
                    <ul className="space-y-2.5 text-sm font-ui">
                        <li><Link to="/shop" className="text-secondary hover:text-[var(--brand)] transition-colors">All Products</Link></li>
                        <li><Link to="/coa" className="text-secondary hover:text-[var(--brand)]">COA Library</Link></li>
                        <li><Link to="/account/orders" className="text-secondary hover:text-[var(--brand)]">Order History</Link></li>
                        <li><Link to="/account/wishlist" className="text-secondary hover:text-[var(--brand)]">Wishlist</Link></li>
                    </ul>
                </div>

                <div className="md:col-span-2">
                    <div className="data-label mb-4">Quality</div>
                    <ul className="space-y-2.5 text-sm font-ui">
                        <li><Link to="/coa" className="text-secondary hover:text-[var(--brand)]">COA Library</Link></li>
                        <li><Link to="/quality" className="text-secondary hover:text-[var(--brand)]">Quality Control</Link></li>
                        <li><Link to="/blog" data-testid="footer-blog-link" className="text-secondary hover:text-[var(--brand)]">Blog</Link></li>
                    </ul>
                </div>

                <div className="md:col-span-2">
                    <div className="data-label mb-4">Account</div>
                    <ul className="space-y-2.5 text-sm font-ui">
                        <li><Link to="/login" className="text-secondary hover:text-[var(--brand)]">Sign In</Link></li>
                        <li><Link to="/register" className="text-secondary hover:text-[var(--brand)]">Create Account</Link></li>
                        <li><Link to="/account" className="text-secondary hover:text-[var(--brand)]">Dashboard</Link></li>
                    </ul>
                </div>

                <div className="md:col-span-2">
                    <div className="data-label mb-4">Compliance</div>
                    <ul className="space-y-2.5 text-sm font-ui">
                        <li><Link to="/about" className="text-secondary hover:text-[var(--brand)]">About Aminoplex</Link></li>
                        <li><Link to="/contact" className="text-secondary hover:text-[var(--brand)]">Contact</Link></li>
                        <li><Link to="/terms" className="text-secondary hover:text-[var(--brand)]">Terms of Sale</Link></li>
                        <li><Link to="/privacy" className="text-secondary hover:text-[var(--brand)]">Privacy Policy</Link></li>
                        <li><Link to="/shipping" className="text-secondary hover:text-[var(--brand)]">Shipping Policy</Link></li>
                        <li><Link to="/refund" className="text-secondary hover:text-[var(--brand)]">Refund Policy</Link></li>
                        <li><Link to="/compliance" className="text-secondary hover:text-[var(--brand)]">Compliance</Link></li>
                    </ul>
                </div>
            </div>

            <div className="hairline" />
            <div className="max-w-7xl mx-auto px-6 lg:px-10 py-6">
                <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-6">
                    <div>
                        <p className="data-label text-brand mb-2">LEGAL ENTITY</p>
                        <p className="text-xs text-secondary font-mono leading-relaxed">
                            JNA Ventures LLC<br/>
                            d/b/a Aminoplex<br/>
                            P.O. Box 491<br/>
                            Haymarket, VA 20168
                        </p>
                    </div>
                    <div>
                        <p className="data-label text-brand mb-2">SUPPORT</p>
                        <p className="text-xs text-secondary font-mono leading-relaxed">
                            <a href="mailto:support@aminoplex.bio" className="hover:text-brand">support@aminoplex.bio</a>
                        </p>
                    </div>
                    <div>
                        <p className="data-label text-brand mb-2">LEGAL</p>
                        <ul className="text-xs text-secondary font-mono space-y-1">
                            <li><Link to="/terms" data-testid="footer-terms" className="hover:text-brand">Terms & Conditions</Link></li>
                            <li><Link to="/privacy" data-testid="footer-privacy" className="hover:text-brand">Privacy Policy</Link></li>
                            <li><Link to="/shipping" data-testid="footer-shipping" className="hover:text-brand">Shipping Policy</Link></li>
                            <li><Link to="/refund" data-testid="footer-refund" className="hover:text-brand">Refund Policy</Link></li>
                            <li><Link to="/compliance" data-testid="footer-compliance" className="hover:text-brand">Research-Use-Only Compliance</Link></li>
                        </ul>
                    </div>
                </div>
                <div className="hairline mb-5" />
                <p data-testid="footer-ruo" className="text-[0.65rem] text-muted font-mono uppercase tracking-widest mb-4 text-center">
                    All products are supplied for Research Use Only — Not for human or animal consumption. Not evaluated by the FDA. Not intended to diagnose, cure, mitigate, treat, or prevent any disease.
                </p>
                <div className="flex flex-col md:flex-row items-center justify-between gap-3">
                    <p className="data-label">© {new Date().getFullYear()} JNA Ventures LLC d/b/a Aminoplex · All Rights Reserved</p>
                    <div className="flex items-center gap-4">
                        <p className="data-label">Zelle</p>
                        {isAdmin && (
                            <>
                                <Link to="/admin" data-testid="footer-admin-link" className="data-label text-[var(--brand)] hover:text-[var(--text-primary)] transition-colors">Admin →</Link>
                                <Link to="/label-designer" data-testid="footer-label-designer-link" className="data-label text-[var(--brand)] hover:text-[var(--text-primary)] transition-colors">Label System →</Link>
                            </>
                        )}
                    </div>
                </div>
            </div>

            <div className="border-t border-[var(--border)] py-5">
                <div className="max-w-7xl mx-auto px-6 lg:px-10 text-center">
                    <p className="data-label text-[var(--text-muted)]" data-testid="footer-credit">
                        Website &amp; SEO management by{" "}
                        <a
                            href="https://webnehtech.com"
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-[var(--brand)] hover:text-[var(--text-primary)] transition-colors"
                        >
                            webnehtech.com
                        </a>
                    </p>
                </div>
            </div>
        </footer>
    );
}
